import { useRouter } from "next/navigation";
import { SESSION_STORAGE_KEYS } from "@/constants/session-storage-keys";
import { useFormContext } from "@/contexts/form";
import { logError } from "@/datadog/logger";
import { LandingPageForm } from "@/features/landing/use-landing-page-form";
import { CacheForms } from "@/types/cache-forms";
import { clearStep, forwardStep } from "@/utils/step-cookie";

/**
 * Create flow for the landing page (no application exists yet).
 *
 * Sends the landing + about form data as a single create body — no follow-up
 * update PUT for the first step — then persists the new id, advances the step
 * cookie, and routes to the application flow at `/{uuid}`.
 */
export const useCreateApplication = () => {
	const router = useRouter();
	const { getValues: getAboutValues } = useFormContext("aboutForm");

	return async (landingPageForm: LandingPageForm) => {
		try {
			const aboutValues = getAboutValues();
			const body: CacheForms = {
				landingPageForm,
				aboutForm: {
					...aboutValues,
					appliedAmount: aboutValues.appliedAmount,
					appliedRepaymentTime: aboutValues.appliedRepaymentTime,
				},
			};

			const res = await fetch("/api/db-operations/create-application", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
			});
			if (!res.ok) {
				throw new Error("Failed to create application");
			}

			const uuid = await res.json();
			clearStep();
			sessionStorage.setItem(
				SESSION_STORAGE_KEYS.CREATED_APPLICATION_ID,
				uuid
			);
			sessionStorage.setItem(
				SESSION_STORAGE_KEYS.HAS_FETCHED_CACHE(uuid),
				"true"
			);
			forwardStep();
			router.push(`/${uuid}`);
		} catch (err) {
			logError("Failed to create application", err, {
				task: "createApplication",
			});
			throw Error("Application created ERROR: " + (err as Error).message);
		}
	};
};
